/**
 * Curated list of NDBC buoy stations relevant to surf forecasting.
 *
 * Each station has coordinates so we can find the nearest one to any
 * location. Stations are categorized as nearshore (< 30km from coast)
 * or offshore.
 *
 * This file is the NDBC-only catalogue and the basin classifier; the
 * provider-agnostic matcher across NDBC + CMEMS lives in
 * `./buoy-stations.ts`.
 */

export interface NDBCStation {
  id: string;
  name: string;
  lat: number;
  lon: number;
  type: "nearshore" | "offshore";
}

export const NDBC_STATIONS: NDBCStation[] = [
  // ── US East Coast ──────────────────────────────────────────────────────────
  {
    id: "44017",
    name: "Montauk Point",
    lat: 40.694,
    lon: -72.048,
    type: "offshore",
  },
  {
    id: "44025",
    name: "Long Island",
    lat: 40.251,
    lon: -73.164,
    type: "offshore",
  },
  {
    id: "44009",
    name: "Delaware Bay",
    lat: 38.464,
    lon: -74.703,
    type: "offshore",
  },
  {
    id: "44014",
    name: "Virginia Beach",
    lat: 36.611,
    lon: -74.836,
    type: "offshore",
  },
  {
    id: "41036",
    name: "Wrightsville Beach",
    lat: 34.207,
    lon: -76.949,
    type: "nearshore",
  },
  {
    id: "41110",
    name: "Myrtle Beach",
    lat: 33.55,
    lon: -77.07,
    type: "nearshore",
  },
  { id: "41004", name: "Edisto", lat: 32.501, lon: -79.099, type: "offshore" },
  {
    id: "41008",
    name: "Grays Reef",
    lat: 31.402,
    lon: -80.869,
    type: "offshore",
  },
  {
    id: "41112",
    name: "WFS Offshore",
    lat: 28.2,
    lon: -80.0,
    type: "offshore",
  },
  {
    id: "41113",
    name: "Cape Canaveral Nearshore",
    lat: 28.4,
    lon: -80.53,
    type: "nearshore",
  },
  {
    id: "41117",
    name: "St Augustine Nearshore",
    lat: 30.0,
    lon: -80.17,
    type: "nearshore",
  },
  {
    id: "41114",
    name: "Fort Pierce",
    lat: 27.551,
    lon: -80.217,
    type: "nearshore",
  },
  {
    id: "41009",
    name: "Canaveral East",
    lat: 28.519,
    lon: -80.185,
    type: "offshore",
  },

  // ── US Gulf Coast ──────────────────────────────────────────────────────────
  {
    id: "42036",
    name: "West Tampa",
    lat: 28.5,
    lon: -84.517,
    type: "offshore",
  },
  {
    id: "42012",
    name: "Orange Beach",
    lat: 30.065,
    lon: -87.555,
    type: "nearshore",
  },
  {
    id: "42035",
    name: "Galveston",
    lat: 29.232,
    lon: -94.413,
    type: "offshore",
  },
  { id: "42019", name: "Freeport", lat: 27.913, lon: -95.36, type: "offshore" },
  {
    id: "42020",
    name: "Corpus Christi",
    lat: 26.966,
    lon: -96.694,
    type: "offshore",
  },

  // ── US West Coast ──────────────────────────────────────────────────────────
  {
    id: "46025",
    name: "Santa Monica Basin",
    lat: 33.749,
    lon: -119.053,
    type: "offshore",
  },
  {
    id: "46221",
    name: "Santa Cruz Basin",
    lat: 33.854,
    lon: -118.634,
    type: "offshore",
  },
  {
    id: "46253",
    name: "San Pedro",
    lat: 33.576,
    lon: -118.181,
    type: "nearshore",
  },
  {
    id: "46222",
    name: "San Pedro South",
    lat: 33.618,
    lon: -118.317,
    type: "offshore",
  },
  {
    id: "46047",
    name: "Tanner Bank",
    lat: 32.433,
    lon: -119.533,
    type: "offshore",
  },
  {
    id: "46086",
    name: "San Clemente Basin",
    lat: 32.499,
    lon: -118.034,
    type: "offshore",
  },
  {
    id: "46011",
    name: "Santa Maria",
    lat: 34.868,
    lon: -120.857,
    type: "offshore",
  },
  {
    id: "46054",
    name: "West Santa Barbara",
    lat: 34.274,
    lon: -120.459,
    type: "offshore",
  },
  {
    id: "46218",
    name: "Harvest",
    lat: 34.454,
    lon: -120.782,
    type: "nearshore",
  },
  {
    id: "46042",
    name: "Monterey",
    lat: 36.789,
    lon: -122.404,
    type: "offshore",
  },
  {
    id: "46012",
    name: "Half Moon Bay",
    lat: 37.363,
    lon: -122.881,
    type: "offshore",
  },
  {
    id: "46026",
    name: "San Francisco",
    lat: 37.759,
    lon: -122.833,
    type: "offshore",
  },
  {
    id: "46013",
    name: "Bodega Bay",
    lat: 38.242,
    lon: -123.301,
    type: "offshore",
  },
  {
    id: "46014",
    name: "Point Arena",
    lat: 39.196,
    lon: -123.969,
    type: "offshore",
  },
  {
    id: "46022",
    name: "Eel River",
    lat: 40.749,
    lon: -124.577,
    type: "offshore",
  },
  {
    id: "46027",
    name: "Crescent City",
    lat: 41.85,
    lon: -124.38,
    type: "offshore",
  },

  // ── Pacific Northwest ──────────────────────────────────────────────────────
  {
    id: "46029",
    name: "Columbia River Bar",
    lat: 46.144,
    lon: -124.485,
    type: "offshore",
  },
  {
    id: "46041",
    name: "Cape Elizabeth",
    lat: 47.353,
    lon: -124.731,
    type: "offshore",
  },
  {
    id: "46005",
    name: "Washington Offshore",
    lat: 46.1,
    lon: -131.0,
    type: "offshore",
  },

  // ── Hawaii ─────────────────────────────────────────────────────────────────
  {
    id: "51201",
    name: "Waimea Bay",
    lat: 21.673,
    lon: -158.116,
    type: "nearshore",
  },
  {
    id: "51202",
    name: "Mokapu Point",
    lat: 21.417,
    lon: -157.68,
    type: "nearshore",
  },
  {
    id: "51003",
    name: "Hawaii SW",
    lat: 19.087,
    lon: -160.66,
    type: "offshore",
  },
  {
    id: "51004",
    name: "Hawaii NW",
    lat: 17.525,
    lon: -152.382,
    type: "offshore",
  },
  {
    id: "51101",
    name: "NW Hawaii",
    lat: 24.361,
    lon: -162.075,
    type: "offshore",
  },

  // ── Alaska / Canada ────────────────────────────────────────────────────────
  {
    id: "46184",
    name: "North Nomad",
    lat: 53.92,
    lon: -138.85,
    type: "offshore",
  },
  {
    id: "46205",
    name: "West Dixon Entrance",
    lat: 54.18,
    lon: -134.32,
    type: "offshore",
  },
  {
    id: "46206",
    name: "La Perouse Bank",
    lat: 48.84,
    lon: -126.0,
    type: "offshore",
  },
  {
    id: "46132",
    name: "South Brooks",
    lat: 49.74,
    lon: -127.93,
    type: "offshore",
  },
  {
    id: "46004",
    name: "Middle Nomad",
    lat: 50.93,
    lon: -136.1,
    type: "offshore",
  },

  // ── Caribbean / Central America ────────────────────────────────────────────
  {
    id: "41043",
    name: "NE Puerto Rico",
    lat: 21.124,
    lon: -64.784,
    type: "offshore",
  },
  {
    id: "41047",
    name: "NE Caribbean",
    lat: 27.469,
    lon: -71.494,
    type: "offshore",
  },
  {
    id: "42057",
    name: "Western Caribbean",
    lat: 16.908,
    lon: -81.422,
    type: "offshore",
  },
  {
    id: "42058",
    name: "Central Caribbean",
    lat: 14.779,
    lon: -75.064,
    type: "offshore",
  },
  {
    id: "42059",
    name: "Eastern Caribbean",
    lat: 15.252,
    lon: -67.483,
    type: "offshore",
  },

  // ── South America ──────────────────────────────────────────────────────────
  {
    id: "41049",
    name: "South Equatorial",
    lat: 0.0,
    lon: -23.535,
    type: "offshore",
  },
  {
    id: "41040",
    name: "NE Brazil",
    lat: -14.56,
    lon: -32.06,
    type: "offshore",
  },

  // ── Pacific Islands ────────────────────────────────────────────────────────
  { id: "52200", name: "Majuro", lat: 8.74, lon: 167.74, type: "nearshore" },
  {
    id: "52211",
    name: "Pago Pago",
    lat: -14.28,
    lon: -170.69,
    type: "nearshore",
  },
];

/**
 * Coarse ocean-basin classifier used to keep the buoy-matcher from
 * jumping over continents. Raw haversine was matching a Naples FL (Gulf
 * side) spot to Fort Pierce FL (Atlantic side) at ~180 km — wrong
 * ocean, wrong swell window. Only enough granularity to separate the
 * obvious cases; anything unclassified falls through to "other" and
 * still uses the simple nearest-station fallback.
 */
export type OceanBasin =
  | "gulf-mexico"
  | "atlantic-us"
  | "pacific-us"
  | "hawaii"
  | "caribbean"
  | "other";

export function classifyBasin(lat: number, lon: number): OceanBasin {
  // Florida peninsula: east coast is Atlantic, west coast is Gulf. A
  // single longitude cutoff doesn't work — Naples (26.14N, -81.80W) is
  // Gulf while Flagler (29.47N, -81.13W) is Atlantic at almost the
  // same longitude. Approximate the peninsula spine as a line from
  // Key West (~24N, -81.3W) to the panhandle bend (~30N, -82.2W) and
  // classify by which side the point falls on.
  if (lat >= 24 && lat <= 31 && lon >= -98 && lon <= -79.5) {
    const spineLon = -81.3 - 0.15 * (lat - 24);
    if (lon < spineLon) return "gulf-mexico";
    return "atlantic-us";
  }
  // Rest of Gulf (panhandle west through TX): clearly Gulf.
  if (lat >= 24 && lat <= 31 && lon >= -98 && lon < -82.5) return "gulf-mexico";
  // US Atlantic north of FL: east coast from GA through ME.
  if (lat > 31 && lat <= 45 && lon >= -82 && lon <= -65) return "atlantic-us";
  // US Pacific: CA/OR/WA + a bit of BC.
  if (lat >= 32 && lat <= 50 && lon >= -130 && lon <= -115) return "pacific-us";
  // Hawaiian chain.
  if (lat >= 18 && lat <= 23 && lon >= -161 && lon <= -154) return "hawaii";
  // Caribbean basin (excludes FL peninsula region handled above).
  if (lat >= 10 && lat < 24 && lon >= -90 && lon <= -60) return "caribbean";
  return "other";
}
